import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

interface NavItem {
  path: string;
  label: string;
}

interface NavDropdown {
  label: string;
  items: NavItem[];
}

type NavConfig = (NavItem | NavDropdown)[];

function isDropdown(item: NavItem | NavDropdown): item is NavDropdown {
  return 'items' in item;
}

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState<Record<string, boolean>>({});
  const closeTimerRef = useRef<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navConfig: NavConfig = [
    {
      label: 'Who We Serve',
      items: [
        { path: '/startups', label: 'Startups' },
        { path: '/technical-blueprint', label: 'Technical Blueprint' },
      ]
    },
    { path: '/how-we-work', label: 'How We Work' },
    { path: '/team', label: 'Team' },
  ];

  const handleDropdownOpen = (dropdown: string) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpenDropdown(dropdown);
  };

  const handleDropdownClose = () => {
    closeTimerRef.current = setTimeout(() => setOpenDropdown(null), 200);
  };

  const cancelCloseTimer = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  const toggleMobileDropdown = (key: string) => {
    setIsMobileDropdownOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileDropdownOpen({});
  };

  const isDropdownActive = (items: NavItem[]) => items.some(item => location.pathname === item.path);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200`}
      style={{
        backgroundColor: 'rgba(15, 25, 35, 0.96)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: isScrolled ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent',
        boxShadow: isScrolled ? '0 4px 24px rgba(0,0,0,0.2)' : 'none',
      }}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            <img src="/brightwings-icon.svg?v=3" alt="BrightWings" className="h-8 md:h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navConfig.map((item) => {
              if (isDropdown(item)) {
                const isActive = isDropdownActive(item.items);
                const dropdownKey = item.label.toLowerCase().replace(/\s+/g, '-');
                const isOpen = openDropdown === dropdownKey;

                return (
                  <div
                    key={dropdownKey}
                    className="relative"
                    onMouseEnter={() => handleDropdownOpen(dropdownKey)}
                    onMouseLeave={handleDropdownClose}
                  >
                    <button
                      className="flex items-center gap-1 text-sm font-medium transition-colors"
                      style={{ color: isActive ? 'var(--bw-blue)' : 'rgba(255,255,255,0.7)' }}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                    >
                      {item.label}
                      <svg
                        className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {isOpen && (
                      <div
                        className="absolute top-full left-0 mt-2 py-2 rounded-xl min-w-[220px]"
                        style={{
                          backgroundColor: 'rgba(20, 32, 44, 0.98)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                          backdropFilter: 'blur(12px)',
                        }}
                        onMouseEnter={cancelCloseTimer}
                        onMouseLeave={handleDropdownClose}
                        role="menu"
                      >
                        {item.items.map((subItem) => {
                          const isSubItemActive = location.pathname === subItem.path;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className="block px-4 py-3 text-sm font-medium transition-colors"
                              style={{
                                color: isSubItemActive ? 'var(--bw-blue)' : 'rgba(255,255,255,0.7)',
                                backgroundColor: isSubItemActive ? 'rgba(0, 163, 224, 0.1)' : 'transparent',
                              }}
                              onMouseEnter={(e) => {
                                if (!isSubItemActive) {
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSubItemActive) {
                                  e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }
                              }}
                              role="menuitem"
                            >
                              {subItem.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              } else {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="text-sm font-medium transition-colors"
                    style={{ color: isActive ? 'var(--bw-blue)' : 'rgba(255,255,255,0.7)' }}
                  >
                    {item.label}
                  </Link>
                );
              }
            })}

            <Link
              to="/contact"
              className="px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 text-white"
              style={{
                backgroundColor: 'var(--bw-blue)',
                boxShadow: '0 2px 8px rgba(0, 163, 224, 0.3)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bw-blue-dark)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bw-blue)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Start a conversation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 transition-colors"
            style={{ color: 'rgba(255,255,255,0.7)' }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 py-4 space-y-2" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            {navConfig.map((item) => {
              if (isDropdown(item)) {
                const dropdownKey = item.label.toLowerCase().replace(/\s+/g, '-');
                const isOpen = isMobileDropdownOpen[dropdownKey];
                const isActive = isDropdownActive(item.items);

                return (
                  <div key={dropdownKey}>
                    <button
                      className="flex items-center justify-between w-full py-2 text-sm font-medium transition-colors"
                      style={{ color: isActive ? 'var(--bw-blue)' : 'rgba(255,255,255,0.7)' }}
                      onClick={() => toggleMobileDropdown(dropdownKey)}
                    >
                      {item.label}
                      <svg className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: isOpen ? '500px' : '0px' }}>
                      <div className="pl-4 mt-2 space-y-2">
                        {item.items.map((subItem) => {
                          const isSubItemActive = location.pathname === subItem.path;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className="block py-2 text-sm transition-colors"
                              style={{ color: isSubItemActive ? 'var(--bw-blue)' : 'rgba(255,255,255,0.65)' }}
                              onClick={closeMobileMenu}
                            >
                              {subItem.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              } else {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block py-2 text-sm font-medium transition-colors"
                    style={{ color: isActive ? 'var(--bw-blue)' : 'rgba(255,255,255,0.7)' }}
                    onClick={closeMobileMenu}
                  >
                    {item.label}
                  </Link>
                );
              }
            })}

            <Link
              to="/contact"
              className="block w-full mt-4 px-5 py-3 rounded-lg font-semibold text-sm text-center transition-all text-white"
              style={{ backgroundColor: 'var(--bw-blue)' }}
              onClick={closeMobileMenu}
            >
              Start a conversation
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
